import { PACKAGE_GROUPS, HOSTING_PLANS } from "@/lib/packages";

export const SITE_URL = "https://equinoxsystems.tech";
const ORG_ID = `${SITE_URL}/#organization`;
const EMAIL = "Equinox@freedomcoreventures.com";
const LOGO = `${SITE_URL}/equinox-logo.png`;

export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": ORG_ID,
    name: "Equinox Systems",
    legalName: "Equinox Systems — A Freedom Core Ventures Company",
    url: SITE_URL,
    logo: LOGO,
    email: EMAIL,
    description:
      "Equinox Systems builds premium custom websites with no upfront website development cost through its Free Custom Website Program, plus web apps, CRMs, CMS platforms, and AI integrations.",
    sameAs: [
      "https://www.instagram.com/equinox_systems/",
      "https://www.facebook.com/profile.php?id=61590765742836",
    ],
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "Sales",
      email: EMAIL,
      areaServed: "US",
      availableLanguage: "English",
    },
  };
}

export function localBusinessSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "@id": `${SITE_URL}/#localbusiness`,
    name: "Equinox Systems",
    image: LOGO,
    url: SITE_URL,
    email: EMAIL,
    priceRange: "$$",
    description:
      "Nationwide remote web development and the Free Custom Website Program for blue-collar, local service, small businesses, startups, and corporate clients across the United States.",
    areaServed: { "@type": "Country", name: "United States" },
    address: { "@type": "PostalAddress", addressCountry: "US" },
    parentOrganization: { "@id": ORG_ID },
  };
}

export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    url: SITE_URL,
    name: "Equinox Systems",
    publisher: { "@id": ORG_ID },
  };
}

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${SITE_URL}${it.path}`,
    })),
  };
}

export function faqSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export function serviceSchema(name, description, path) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: name,
    name,
    description,
    url: `${SITE_URL}${path}`,
    provider: { "@id": ORG_ID },
    areaServed: { "@type": "Country", name: "United States" },
  };
}

export function freeProgramOfferSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Offer",
    name: "Free Custom Website Program",
    description:
      "Premium custom website with $0 upfront build cost. Hosting and maintenance at $200/month with a 6-month minimum commitment and ownership transfer included.",
    price: "0",
    priceCurrency: "USD",
    url: `${SITE_URL}/apply`,
    category: "Web Development",
    seller: { "@id": ORG_ID },
  };
}

export function offerCatalogSchema() {
  const items = [];
  PACKAGE_GROUPS.forEach((group) => {
    group.packages.forEach((p) => {
      items.push({
        "@type": "Offer",
        name: p.name,
        category: group.title,
        priceCurrency: "USD",
        price: String(p.price).replace(/[^0-9]/g, "") || undefined,
        priceSpecification:
          p.custom || !/[0-9]/.test(String(p.price))
            ? undefined
            : { "@type": "PriceSpecification", price: String(p.price).replace(/[^0-9]/g, ""), priceCurrency: "USD" },
        itemOffered: { "@type": "Service", name: p.name, description: (p.features || []).join(", ") },
      });
    });
  });
  HOSTING_PLANS.forEach((p) => {
    items.push({
      "@type": "Offer",
      name: p.name,
      category: "Hosting & Maintenance",
      priceCurrency: "USD",
      price: String(p.price).replace(/[^0-9]/g, ""),
      itemOffered: { "@type": "Service", name: p.name, description: (p.features || []).join(", ") },
    });
  });
  return {
    "@context": "https://schema.org",
    "@type": "OfferCatalog",
    name: "Equinox Systems Packages & Pricing",
    url: `${SITE_URL}/packages`,
    provider: { "@id": ORG_ID },
    itemListElement: items,
  };
}
